import { Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import "modules/BrandsDashboard/components/BrandModal/BrandModal.scss";
import { BrandForm } from "modules/BrandsDashboard/components/BrandModal/components";
import { useCreateBrandMutation, useUpdateBrandMutation } from "modules/BrandsDashboard/components/BrandModal/hooks";
import { useBrandGroupsQuery, useBrandsQuery, useUploadBrandLogoMutation } from "modules/BrandsDashboard/hooks";
import { isValidUrl } from "modules/BrandsDashboard/utils";

import type { Brand, BrandGroup } from "models";
import type { BrandFormValues } from "modules/BrandsDashboard/components/BrandModal/components";
import type { BrandFormPayload } from "modules/BrandsDashboard/components/BrandModal/hooks";

type BrandModalProps = {
  isOpen: boolean;
  brand: Brand | null;
  brandGroupId?: number;
  onClose: () => void;
};

export const isBrandNameCountryTaken = (brands: Brand[], { id, name, country }: Pick<Brand, "id" | "name" | "country">) => {
  const trimmedBrandName = name.trim().toLowerCase();
  const trimmedCountryName = country.trim().toLowerCase();
  if (!trimmedBrandName || !trimmedCountryName) return false;

  return brands.some(brand => brand.id !== id && brand.name.trim().toLowerCase() === trimmedBrandName && brand.country.trim().toLowerCase() === trimmedCountryName);
};

const clearUnchangedGroupSettings = (payload: BrandFormPayload, brandGroup?: BrandGroup) => {
  if (!brandGroup) return payload;

  return {
    ...payload,
    country: payload.country === brandGroup.country ? "" : payload.country,
    button_label: payload.button_label === brandGroup.button_label ? "" : payload.button_label,
    color: payload.color === brandGroup.color ? "" : payload.color,
    logo: payload.logo === brandGroup.logo ? "" : payload.logo,
    tablet_service_button_url: payload.tablet_service_button_url === brandGroup.tablet_service_button_url ? "" : payload.tablet_service_button_url,
    servicebox_wo: payload.servicebox_wo === brandGroup.servicebox_wo ? "" : payload.servicebox_wo,
    servicebox_vin: payload.servicebox_vin === brandGroup.servicebox_vin ? "" : payload.servicebox_vin,
    servicebox_wo_2: payload.servicebox_wo_2 === brandGroup.servicebox_wo_2 ? "" : payload.servicebox_wo_2,
    servicebox_wo_2_name: payload.servicebox_wo_2_name === brandGroup.servicebox_wo_2_name ? "" : payload.servicebox_wo_2_name
  };
};

const isInvalidForm = ({ name, vin_prefix, servicebox_wo, servicebox_wo_2, servicebox_vin, tablet_service_button_url }: Omit<BrandFormPayload, "brand_group_id">) => {
  const areRequiredFieldsValid = Boolean(name.trim() && vin_prefix);
  const areUrlsValid =
    (!servicebox_wo || isValidUrl(servicebox_wo)) &&
    (!servicebox_wo_2 || isValidUrl(servicebox_wo_2)) &&
    (!servicebox_vin || isValidUrl(servicebox_vin)) &&
    (!tablet_service_button_url || isValidUrl(tablet_service_button_url));

  return !(areRequiredFieldsValid && areUrlsValid);
};

const FormHelper = createFormHelper<BrandFormValues>();

export const BrandModal = ({ isOpen, brand, brandGroupId, onClose }: BrandModalProps) => {
  const { data: brands = [] } = useBrandsQuery();
  const { data: brandGroups = [] } = useBrandGroupsQuery();

  const { mutateAsync: createBrand, isPending: isCreateBrandPending } = useCreateBrandMutation();
  const { mutateAsync: updateBrand, isPending: isUpdateBrandPending } = useUpdateBrandMutation();
  const { mutateAsync: uploadBrandLogo, isPending: isUploadLogoPending } = useUploadBrandLogoMutation();

  const brandGroup = brandGroups.find(group => group.id === (brand?.brand_group_id ?? brandGroupId));

  const handleSubmit = async ({ logoFile, brand_group_id, ...payload }: BrandFormValues) => {
    if (!brand_group_id || isInvalidForm(payload)) return;
    if (isBrandNameCountryTaken(brands, payload)) return;

    let logo = payload.logo;

    if (logoFile) {
      const formData = new FormData();
      formData.append("file", logoFile);
      formData.append("path", `${payload.vin_prefix}_${payload.name}`);
      logo = await uploadBrandLogo(formData);
    }

    const brandPayload = clearUnchangedGroupSettings(
      { ...payload, logo, brand_group_id },
      brandGroups.find(group => group.id === brand_group_id)
    );

    if (brand !== null) await updateBrand(brandPayload);
    else await createBrand(brandPayload);
    onClose();
  };

  return (
    <Modal open={isOpen} size="large" className="BrandModal">
      <FormHelper.Form
        onSubmit={handleSubmit}
        defaultValues={{
          id: brand?.id ?? -1,
          name: brand?.name ?? "",
          country: brand?.country || brandGroup?.country || "",
          vin_prefix: brand?.vin_prefix ?? "",
          button_label: brand?.button_label || brandGroup?.button_label || (brand ? "" : "Service"),
          pon_brand_code: brand?.pon_brand_code ?? "",
          tablet_service_button_url: brand?.tablet_service_button_url || brandGroup?.tablet_service_button_url || "",
          servicebox_wo: brand?.servicebox_wo || brandGroup?.servicebox_wo || "",
          servicebox_vin: brand?.servicebox_vin || brandGroup?.servicebox_vin || "",
          logo: brand?.logo || brandGroup?.logo || "",
          color: brand?.color || brandGroup?.color || "",
          is_active: brand?.is_active ?? false,
          logoFile: null,
          servicebox_wo_2: brand?.servicebox_wo_2 || brandGroup?.servicebox_wo_2 || "",
          servicebox_wo_2_name: brand?.servicebox_wo_2_name || brandGroup?.servicebox_wo_2_name || "",
          brand_group_id: brand?.brand_group_id ?? brandGroupId
        }}
      >
        <BrandForm isEdit={brand !== null} isPending={isCreateBrandPending || isUpdateBrandPending || isUploadLogoPending} onClose={onClose} />
      </FormHelper.Form>
    </Modal>
  );
};
